/**
 * Shared Auto-assign Medical Assistant Utilities
 *
 * Common functions used by both legacy and fair assignment implementations.
 * This consolidates duplicate logic across different assignment strategies.
 */
import {
  AppointmentType,
  Language,
  MedicalAssistant,
  SpecialtyType,
} from "~/api/API";
import { FALLBACK_MA_BY_ENV } from "./autoAssignMAConstants";

/**
 * Checks if an appointment type is eligible for auto-assignment
 */
export const isEligibleAppointmentType = (
  appointmentType: AppointmentType | null,
): boolean => {
  if (!appointmentType) return false;
  return [
    AppointmentType.QME,
    AppointmentType.AME,
    AppointmentType.QME_REEVALUATION,
    AppointmentType.AME_REEVALUATION,
    AppointmentType.SIBTF,
    AppointmentType.SIBTF_REEVALUATION,
    AppointmentType.IME_ADR,
    AppointmentType.IME_ADR_REEVALUATION,
  ].includes(appointmentType);
};

/**
 * Gets MAs eligible for the given language
 */
export const getEligibleMAs = (
  allAssistants: MedicalAssistant[] | null,
  language: Language,
): MedicalAssistant[] => {
  if (!allAssistants) return [];

  return allAssistants.filter((ma) => {
    // Skip inactive MAs
    if (ma.inactive) return false;

    // Check if MA supports the required language
    return Boolean(ma.languages?.includes(language));
  });
};

/**
 * Gets the display name for an MA by ID
 */
export const getMADisplayName = (
  maId: string,
  allAssistants: MedicalAssistant[] | null,
): string => {
  if (!allAssistants) return "Unknown";

  const ma = allAssistants.find((assistant) => assistant.id === maId);
  return ma?.name || "Unknown";
};

/**
 * Gets MAs with their language weights, filtered to exclude zero weights
 */
export const getMAsWithWeights = (
  eligibleMAs: MedicalAssistant[],
  language: Language,
): Array<{ ma: MedicalAssistant; weight: number }> => {
  const masWithWeights = eligibleMAs.map((ma) => {
    let weight = 0.0; // Default weight (0.0 = 0%)

    if (ma.assignmentCriteria?.languageWeights && language) {
      const languageWeight = ma.assignmentCriteria.languageWeights.find(
        (lw) => lw.language === language,
      );
      if (languageWeight) {
        weight = languageWeight.weight;
      }
    }

    return { ma, weight };
  });

  // Filter out MAs with weight 0.0 (0% availability)
  return masWithWeights.filter(({ weight }) => weight > 0);
};

/**
 * Common result interface for MA assignment
 */
export interface AutoAssignMAResult {
  shouldAssign: boolean;
  selectedMAId: string | null;
  reason: string;
}

/**
 * Selects an MA using weighted random selection
 */
export const selectMAByWeight = (
  masWithWeights: Array<{ ma: MedicalAssistant; weight: number }>,
): string | null => {
  if (!masWithWeights.length) return null;

  const totalWeight = masWithWeights.reduce(
    (sum, { weight }) => sum + weight,
    0,
  );
  if (totalWeight === 0) return null;

  const randomNum = Math.random() * totalWeight;
  let cumulativeWeight = 0;

  for (const { ma, weight } of masWithWeights) {
    cumulativeWeight += weight;
    if (randomNum < cumulativeWeight) {
      return ma.id;
    }
  }

  // Fallback to last MA
  return masWithWeights[masWithWeights.length - 1]?.ma.id || null;
};

/**
 * Checks if the current MA supports the given language
 */
export const currentMASupportsLanguage = (
  currentMAId: string | null,
  allAssistants: MedicalAssistant[] | null,
  language: Language,
): boolean => {
  if (!currentMAId || !allAssistants) return false;

  const currentMA = allAssistants.find((ma) => ma.id === currentMAId);
  return Boolean(
    currentMA && !currentMA.inactive && currentMA.languages?.includes(language),
  );
};

/**
 * Common validation for assignment prerequisites
 */
export const validateAssignmentPrerequisites = (
  appointmentType: AppointmentType | null,
  patientLanguage: Language | null,
  allAssistants: MedicalAssistant[] | null,
): { isValid: boolean; reason: string } => {
  if (!allAssistants?.length) {
    return { isValid: false, reason: "No medical assistants available" };
  }

  if (!isEligibleAppointmentType(appointmentType)) {
    return {
      isValid: false,
      reason: "Appointment type not eligible for auto-assignment",
    };
  }

  const effectiveLanguage = patientLanguage || Language.ENGLISH;
  const eligibleMAs = getEligibleMAs(allAssistants, effectiveLanguage);

  if (eligibleMAs.length === 0) {
    return {
      isValid: false,
      reason: `No medical assistants available for ${effectiveLanguage}`,
    };
  }

  return { isValid: true, reason: "Prerequisites validated" };
};

/**
 * Gets MAs eligible for the given language and doctor specialties
 * Returns MAs that have either specialty-specific OR global weights for the language
 */
export const getSpecialtyEligibleMAs = (
  allAssistants: MedicalAssistant[] | null,
  language: Language,
  doctorSpecialties: SpecialtyType[] | null,
): MedicalAssistant[] => {
  const basicEligible = getEligibleMAs(allAssistants, language);

  return basicEligible.filter((ma) => {
    if (!ma.assignmentCriteria?.languageWeights) return false;

    // Check if MA has weights for this language and any of the doctor's specialties OR global weights
    return ma.assignmentCriteria.languageWeights.some((lw) => {
      const languageMatch = lw.language === language;
      const weightValid = lw.weight > 0;
      const isGlobalWeight =
        lw.doctorSpecialty === null || lw.doctorSpecialty === undefined;
      const isSpecialtyMatch =
        doctorSpecialties &&
        lw.doctorSpecialty &&
        doctorSpecialties.includes(lw.doctorSpecialty as SpecialtyType);

      return (
        languageMatch && weightValid && (isGlobalWeight || isSpecialtyMatch)
      );
    });
  });
};

/**
 * Gets MAs with their specialty-specific weights, with fallback to global weights
 * Priority: specialty-specific weights > global weights (doctorSpecialty = null)
 */
export const getMAsWithSpecialtyWeights = (
  eligibleMAs: MedicalAssistant[],
  language: Language,
  doctorSpecialties: SpecialtyType[] | null,
): Array<{ ma: MedicalAssistant; weight: number }> => {
  const masWithWeights = eligibleMAs
    .map((ma) => {
      let selectedWeight = 0;
      let hasSpecialtySpecificWeight = false;
      let globalWeight = 0;

      if (ma.assignmentCriteria?.languageWeights) {
        for (const lw of ma.assignmentCriteria.languageWeights) {
          if (lw.language === language) {
            if (
              lw.doctorSpecialty &&
              doctorSpecialties?.includes(lw.doctorSpecialty)
            ) {
              // Found specialty-specific weight
              selectedWeight = Math.max(selectedWeight, lw.weight);
              hasSpecialtySpecificWeight = true;
            } else if (
              lw.doctorSpecialty === null ||
              lw.doctorSpecialty === undefined
            ) {
              // Store global weight as fallback
              globalWeight = Math.max(globalWeight, lw.weight);
            }
          }
        }

        // Use specialty-specific weight if found, otherwise fall back to global weight
        if (!hasSpecialtySpecificWeight && globalWeight > 0) {
          selectedWeight = globalWeight;
        }
      }

      return {
        ma,
        weight: selectedWeight,
        hasSpecialtyWeight: hasSpecialtySpecificWeight,
      };
    })
    .filter(({ weight }) => weight > 0);

  // Prioritize specialty-specific weights: only return global weights if no specialty weights exist
  const specialtyWeightMAs = masWithWeights.filter(
    ({ hasSpecialtyWeight }) => hasSpecialtyWeight,
  );

  if (specialtyWeightMAs.length > 0) {
    return specialtyWeightMAs.map(({ ma, weight }) => ({ ma, weight }));
  } else {
    return masWithWeights.map(({ ma, weight }) => ({ ma, weight }));
  }
};

/**
 * Gets environment-specific fallback MA if available and supports the required language
 */
export const getFallbackMA = (
  allAssistants: MedicalAssistant[] | null,
  language: Language,
): MedicalAssistant | null => {
  if (!allAssistants) return null;

  // Get current environment
  const currentEnv = process.env.REACT_APP_ENV || "dev";
  const fallbackMAId = FALLBACK_MA_BY_ENV[currentEnv];

  // No fallback configured for this environment
  if (!fallbackMAId) return null;

  const fallbackMA = allAssistants.find((ma) => ma.id === fallbackMAId);
  if (
    fallbackMA &&
    !fallbackMA.inactive &&
    fallbackMA.languages?.includes(language)
  ) {
    return fallbackMA;
  }
  return null;
};
